import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import {
  InlineCopyContext,
  PhrasingHostContext,
} from '@/hen-ui/lib/inline-copy';
import { cn, resolveAnchorId } from '@/hen-ui/utils';
import {
  flexVariants,
  getFlexChildrenRatioClasses,
  getFlexJustifyForOverflow,
  getFlexOverflowClasses,
  getFlexShrinkClass,
  type FlexChildrenRatio,
  type FlexOverflow,
  type FlexShrink,
  type FlexVariantProps,
} from './flex.variants';

export interface FlexProps
  extends React.HTMLAttributes<HTMLElement>,
    FlexVariantProps {
  /** Render as child element using Radix Slot */
  asChild?: boolean;
  /**
   * Widths for direct children, applied wherever the Flex is a row
   * (including rows set through `direction-md` / `direction-lg`).
   * Fraction values split two children.
   */
  childrenRatio?: FlexChildrenRatio;
  /**
   * Scroll or clip content that does not fit. Omit or use `visible` to
   * leave overflow unchanged.
   */
  overflow?: FlexOverflow;
  /**
   * Whether a parent row can narrow this layout. Leave unset to allow
   * shrinking. `0` keeps the width. `1` allows shrinking and overrides the
   * fixed width from Width max-content.
   */
  shrink?: FlexShrink | '';
  /**
   * In-page jump target. Rendered as the root element's `id`.
   * A leading `#` is ignored. Blank values are omitted.
   * When both this and `id` are set, this wins.
   */
  anchorId?: string;
}

/**
 * Flex - Flexible layout component for vertical and horizontal arrangements
 *
 * Consolidates Stack (column) and Cluster (row) patterns into a single component.
 * Use `direction="column"` for vertical stacking, `direction="row"` for horizontal layouts.
 *
 * @example Vertical stack (replaces Stack)
 * ```tsx
 * <Flex direction="column" gap="lg">
 *   <Heading variant="h3">Title</Heading>
 *   <Text variant="b3">Description</Text>
 *   <Button>Action</Button>
 * </Flex>
 * ```
 *
 * @example Horizontal layout (replaces Cluster)
 * ```tsx
 * <Flex direction="row" gap="md">
 *   <Button variant="primaryFill">Primary</Button>
 *   <Button variant="textOnly">Secondary</Button>
 * </Flex>
 * ```
 *
 * @example Wrapping tags
 * ```tsx
 * <Flex direction="row" gap="sm" wrap="wrap">
 *   <Badge>Tag 1</Badge>
 *   <Badge>Tag 2</Badge>
 *   <Badge>Tag 3</Badge>
 * </Flex>
 * ```
 *
 * @example Centered content
 * ```tsx
 * <Flex direction="row" justify="center" gap="lg">
 *   <Icon name="star" />
 *   <Text>Centered content</Text>
 * </Flex>
 * ```
 *
 * @example Responsive direction (column on mobile, row from md up, like Grid)
 * ```tsx
 * <Flex direction="column" direction-md="row" gap="md">
 *   <span>Item 1</span>
 *   <span>Item 2</span>
 * </Flex>
 * ```
 *
 * @example Unequal widths for two blocks in a row
 * ```tsx
 * <Flex direction="row" gap="sm" childrenRatio="3/4-1/4" width="full">
 *   <Text variant="l1">Estimated payment</Text>
 *   <Text variant="b3">$412</Text>
 * </Flex>
 * ```
 *
 * @example Filter row that stays on one line and scrolls
 * ```tsx
 * <Flex
 *   direction="row"
 *   wrap="nowrap"
 *   gap="sm"
 *   width="full"
 *   align="center"
 *   overflow="overflow-x: auto"
 * >
 *   <MultiSelect placeholder="Subject" />
 *   <MultiSelect placeholder="Skills" />
 * </Flex>
 * ```
 */
export const Flex = React.forwardRef<HTMLElement, FlexProps>(
  (
    {
      className,
      direction = 'column',
      'direction-md': directionMd,
      'direction-lg': directionLg,
      gap = 'md',
      wrap = 'nowrap',
      align = 'stretch',
      'align-md': alignMd,
      justify = 'start',
      childrenFill = false,
      childrenRatio,
      overflow,
      shrink,
      width,
      height = 'auto',
      asChild = false,
      children,
      anchorId,
      id,
      ...props
    },
    ref,
  ) => {
    const anchor = resolveAnchorId(anchorId, id);
    const inlineCopy = React.useContext(InlineCopyContext);
    const phrasingHost = React.useContext(PhrasingHostContext);
    const Comp = (
      asChild ? Slot : inlineCopy || phrasingHost ? 'span' : 'div'
    ) as React.ElementType;
    const effectiveChildrenFill =
      childrenRatio && direction === 'row' ? false : childrenFill;
    const justifyForOverflow = getFlexJustifyForOverflow(
      justify,
      overflow,
      direction,
    );

    return (
      <Comp
        className={cn(
          flexVariants({
            direction,
            'direction-md': directionMd,
            'direction-lg': directionLg,
            gap,
            wrap,
            align,
            'align-md': alignMd,
            justify: justifyForOverflow.justify,
            childrenFill: effectiveChildrenFill,
            width,
            height,
          }),
          justifyForOverflow.className,
          getFlexChildrenRatioClasses(
            childrenRatio,
            direction,
            directionMd,
            directionLg,
            wrap,
          ),
          getFlexOverflowClasses(overflow),
          getFlexShrinkClass(shrink),
          className,
        )}
        ref={ref}
        {...props}
        {...(anchor ? { id: anchor } : {})}
      >
        {children}
      </Comp>
    );
  },
);

Flex.displayName = 'Flex';
